import { useState, useEffect, useCallback } from 'react'
import { authApi, orgApi } from '../api'
import { useAuth } from '../AuthContext'
import { useEscapeKey } from '../useEscapeKey'
import { Pill } from './Pill'
import { enumLabel, useI18n, type MessageKey } from '../i18n'
import type { User, UserRole, Organization } from '../types'

/** Doit rester aligné sur PASSWORD_MIN_LENGTH dans `src/auth/auth.dto.ts`. */
const PASSWORD_MIN_LENGTH = 12

const ROLE_VARIANTS: Record<UserRole, 'critical' | 'warning' | 'info'> = { SUPERADMIN: 'critical', OWNER: 'critical', ADMIN: 'warning', AGENT: 'info' }

/**
 * Onglets attribuables à un agent. La clé est celle du serveur (`allowedTabs`)
 * et ne change jamais ; seul le libellé suit la langue.
 */
const ALL_TABS: Array<{ key: string; labelKey: MessageKey }> = [
  { key: 'merchants', labelKey: 'module.merchants' },
  { key: 'discovery', labelKey: 'module.discovery' },
  { key: 'webscan', labelKey: 'module.webscan' },
  { key: 'ads', labelKey: 'module.ads' },
  { key: 'betting', labelKey: 'module.betting' },
  { key: 'brands', labelKey: 'module.brands' },
  { key: 'rules', labelKey: 'module.rules' },
]

const ALL_COUNTRY_CODES = ['SN', 'CI', 'ML', 'BF', 'GN', 'TG', 'BJ', 'NE', 'CM', 'GA', 'GH']

export function UsersManager() {
  const { user: currentUser, isSuperAdmin, orgs } = useAuth()
  const { t, fmt } = useI18n()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState({ email: '', fullName: '', password: '', role: 'AGENT' as UserRole, allowedCountries: [] as string[], allowedTabs: [] as string[] })
  const [error, setError] = useState<string | null>(null)
  const [filterOrgId, setFilterOrgId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await authApi.getUsers()
      setUsers(data)
    } catch {
      setError(t('users.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { load() }, [load])

  const resetForm = useCallback(() => {
    setForm({ email: '', fullName: '', password: '', role: 'AGENT', allowedCountries: [], allowedTabs: [] })
    setEditId(null)
    setShowForm(false)
    setError(null)
  }, [])

  useEscapeKey(showForm, resetForm)

  const startEdit = (u: User) => {
    setForm({ email: u.email, fullName: u.fullName, password: '', role: u.role, allowedCountries: u.allowedCountries ?? [], allowedTabs: u.allowedTabs ?? [] })
    setEditId(u.id)
    setShowForm(true)
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      if (editId) {
        const data: Record<string, any> = {
          email: form.email, fullName: form.fullName, role: form.role,
          allowedCountries: form.allowedCountries, allowedTabs: form.allowedTabs,
        }
        if (form.password) data.password = form.password
        await authApi.updateUser(editId, data)
      } else {
        if (!form.password) { setError(t('users.passwordRequired')); return }
        await authApi.createUser({ ...form, organizationId: currentUser?.organizationId ?? null })
      }
      resetForm()
      load()
    } catch (err: any) {
      setError(err.message ?? t('users.genericError'))
    }
  }

  const toggleActive = async (u: User) => {
    try {
      await authApi.updateUser(u.id, { isActive: !u.isActive })
      load()
    } catch (err: any) {
      setError(err.message ?? t('users.genericError'))
    }
  }

  const handleDelete = async (u: User) => {
    if (!confirm(t('users.deleteConfirm', { name: u.fullName }))) return
    try {
      await authApi.deleteUser(u.id)
      load()
    } catch (err: any) {
      setError(err.message ?? t('users.genericError'))
    }
  }

  if (loading) return <p>{t('common.loading')}</p>

  return (
    <div className="section-card">
      <div className="section-header">
        <h2>{t('users.title')}</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {isSuperAdmin && orgs.length > 0 && (
            <select
              className="select-period"
              value={filterOrgId ?? ''}
              onChange={(e) => setFilterOrgId(e.target.value || null)}
            >
              <option value="">{t('users.allOrgs')}</option>
              {orgs.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          )}
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true) }}>
            {t('users.newUser')}
          </button>
        </div>
      </div>

      {error && !showForm && <div className="alert-error">{error}</div>}

      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h3>{editId ? t('users.formEdit') : t('users.formNew')}</h3>
                <button type="button" className="modal-close" onClick={resetForm} aria-label={t('common.close')}>&times;</button>
              </div>
              <div className="modal-body">
                {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}
                <div className="user-form-grid">
                  <label>
                    {t('users.fullName')}
                    <input
                      type="text"
                      value={form.fullName}
                      onChange={(e) => setForm(f => ({ ...f, fullName: e.target.value }))}
                      required
                      autoFocus
                    />
                  </label>
                  <label>
                    {t('common.email')}
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    {editId ? t('users.passwordUnchanged') : t('users.passwordMin', { n: PASSWORD_MIN_LENGTH })}
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                      {...(!editId ? { required: true } : {})}
                      minLength={PASSWORD_MIN_LENGTH}
                    />
                  </label>
                  <label>
                    {t('common.role')}
                    <select
                      value={form.role}
                      onChange={(e) => setForm(f => ({ ...f, role: e.target.value as UserRole }))}
                    >
                      <option value="AGENT">{t('role.AGENT')}</option>
                      <option value="ADMIN">{t('role.ADMIN')}</option>
                      {(isSuperAdmin) && <option value="OWNER">{t('role.OWNER')}</option>}
                      {(isSuperAdmin) && <option value="SUPERADMIN">{t('role.SUPERADMIN')}</option>}
                    </select>
                  </label>
                </div>
                {form.role === 'SUPERADMIN' && (
                  <p className="user-perm-note">{t('users.superadminNote')}</p>
                )}
                {form.role === 'AGENT' && (
                  <div className="user-permissions">
                    <div className="user-perm-group">
                      <label className="user-perm-label">
                        {t('users.allowedCountries')}
                        <span className="user-perm-hint">{t('users.allowedCountriesHint')}</span>
                      </label>
                      <div className="user-perm-checks">
                        {ALL_COUNTRY_CODES.map(code => (
                          <label key={code} className="user-perm-check">
                            <input
                              type="checkbox"
                              checked={form.allowedCountries.includes(code)}
                              onChange={(e) => setForm(f => ({
                                ...f,
                                allowedCountries: e.target.checked
                                  ? [...f.allowedCountries, code]
                                  : f.allowedCountries.filter(x => x !== code),
                              }))}
                            />
                            {enumLabel(t, 'country', code, code)}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="user-perm-group">
                      <label className="user-perm-label">
                        {t('users.allowedTabs')}
                        <span className="user-perm-hint">{t('users.allowedTabsHint')}</span>
                      </label>
                      <div className="user-perm-checks">
                        {ALL_TABS.map(tab => (
                          <label key={tab.key} className="user-perm-check">
                            <input
                              type="checkbox"
                              checked={form.allowedTabs.includes(tab.key)}
                              onChange={(e) => setForm(f => ({
                                ...f,
                                allowedTabs: e.target.checked
                                  ? [...f.allowedTabs, tab.key]
                                  : f.allowedTabs.filter(x => x !== tab.key),
                              }))}
                            />
                            {t(tab.labelKey)}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {editId ? t('common.save') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">{t('common.name')}</th>
            <th scope="col">{t('common.email')}</th>
            <th scope="col">{t('common.role')}</th>
            {isSuperAdmin && <th scope="col">{t('common.organization')}</th>}
            <th scope="col">{t('users.colPermissions')}</th>
            <th scope="col">{t('common.status')}</th>
            <th scope="col">{t('common.createdAt')}</th>
            <th scope="col">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {users
            .filter(u => !filterOrgId || u.organizationId === filterOrgId)
            .map((u) => (
            <tr key={u.id} className={u.isActive ? '' : 'user-inactive'}>
              <td>{u.fullName}</td>
              <td>{u.email}</td>
              <td><Pill label={enumLabel(t, 'role', u.role, u.role)} variant={ROLE_VARIANTS[u.role]} title={u.role} /></td>
              {isSuperAdmin && (
                <td>
                  {u.organizationId
                    ? (orgs.find(o => o.id === u.organizationId)?.name ?? u.organizationId.slice(0, 8))
                    : <span style={{ opacity: 0.5 }}>—</span>}
                </td>
              )}
              <td className="user-perms-cell">
                {(u.role === 'SUPERADMIN' || u.role === 'OWNER' || u.role === 'ADMIN') ? (
                  <span className="user-perm-all">{t('users.fullAccess')}</span>
                ) : (
                  <div className="user-perm-tags">
                    {(u.allowedCountries?.length ?? 0) === 0 && (u.allowedTabs?.length ?? 0) === 0 ? (
                      <span className="user-perm-all">{t('users.allCountriesTabs')}</span>
                    ) : (
                      <>
                        {(u.allowedCountries?.length ?? 0) > 0 && (
                          <span className="user-perm-tag">{u.allowedCountries.join(', ')}</span>
                        )}
                        {(u.allowedTabs?.length ?? 0) > 0 && (
                          <span className="user-perm-tag">{t('users.tabCount', { n: u.allowedTabs.length })}</span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </td>
              <td>
                <Pill
                  label={u.isActive ? t('users.active') : t('users.disabled')}
                  variant={u.isActive ? 'ok' : 'warning'}
                />
              </td>
              <td>{fmt.date(u.createdAt)}</td>
              <td>
                <button className="btn-icon" title={t('common.edit')} aria-label={t('users.editAria', { name: u.fullName })} onClick={() => startEdit(u)}>✏️</button>
                <button
                  className="btn-icon"
                  title={u.isActive ? t('users.deactivate') : t('users.activate')}
                  aria-label={t('users.toggleAria', { action: u.isActive ? t('users.deactivate') : t('users.activate'), name: u.fullName })}
                  onClick={() => toggleActive(u)}
                >
                  {u.isActive ? '🚫' : '✅'}
                </button>
                <button className="btn-icon" title={t('common.delete')} aria-label={t('users.deleteAria', { name: u.fullName })} onClick={() => handleDelete(u)}>🗑️</button>
              </td>
            </tr>
          ))}
          {users.filter(u => !filterOrgId || u.organizationId === filterOrgId).length === 0 && (
            <tr><td colSpan={isSuperAdmin ? 8 : 7} className="table-empty">{t('users.empty')}</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
}
